// Mirrors lib/levels/level_generator.dart's "cave" algorithm (cellular
// automata + flood fill) 1:1, so an auto-generated level here reads like
// the ones the game itself procedurally generates for uncurated level ids -
// dense clusters reachable only through a narrow top-center entrance, with
// permanent bricks forming the surrounding walls, every destructible brick
// guaranteed reachable by construction.

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Most filled cells are plain bricks; a minority become multi-hit or
// explosive for variety, matching LevelGenerator._pickBrickCode.
function pickBrickCode(rng) {
  const roll = rng();
  if (roll < 0.08) return 4; // explosive
  if (roll < 0.08 + 0.18) return 3; // multi-hit
  return 1; // normal
}

function smooth(grid, rows, cols) {
  const next = [];
  for (let r = 0; r < rows; r++) {
    const row = [];
    for (let c = 0; c < cols; c++) {
      let solidNeighbors = 0;
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) continue;
          const nr = r + dr;
          const nc = c + dc;
          if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) {
            solidNeighbors++; // out-of-bounds counts as solid -> walled edges
          } else if (grid[nr][nc]) {
            solidNeighbors++;
          }
        }
      }
      row.push(solidNeighbors >= 5);
    }
    next.push(row);
  }
  return next;
}

function floodFillOpen(solid, rows, cols, starts) {
  const visited = Array.from({ length: rows }, () => Array(cols).fill(false));
  const queue = [];
  for (const [sr, sc] of starts) {
    if (!solid[sr][sc] && !visited[sr][sc]) {
      visited[sr][sc] = true;
      queue.push([sr, sc]);
    }
  }
  const dirs = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];
  let head = 0;
  while (head < queue.length) {
    const [r, c] = queue[head++];
    for (const [dr, dc] of dirs) {
      const nr = r + dr;
      const nc = c + dc;
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      if (visited[nr][nc] || solid[nr][nc]) continue;
      visited[nr][nc] = true;
      queue.push([nr, nc]);
    }
  }
  return visited;
}

function attempt(rng, rows, cols, smoothIterations, initialSolidChance, brickFillChance) {
  let solid = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => rng() < initialSolidChance)
  );

  for (let i = 0; i < smoothIterations; i++) {
    solid = smooth(solid, rows, cols);
  }

  const entranceCol = Math.floor(cols / 2);
  const entranceCells = [[0, entranceCol]];
  if (entranceCol + 1 < cols) entranceCells.push([0, entranceCol + 1]);
  entranceCells.push([1, entranceCol]);
  for (const [r, c] of entranceCells) {
    solid[r][c] = false;
  }

  const reachable = floodFillOpen(solid, rows, cols, entranceCells);
  const protectedEntrance = new Set(entranceCells.map(([r, c]) => `${r},${c}`));
  const grid = Array.from({ length: rows }, () => Array(cols).fill(0));

  let normalBrickCount = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (solid[r][c]) {
        grid[r][c] = 2; // permanent wall
      } else if (reachable[r][c]) {
        const key = `${r},${c}`;
        if (!protectedEntrance.has(key) && r >= 2 && rng() < brickFillChance) {
          grid[r][c] = pickBrickCode(rng); // reachable by construction
          normalBrickCount++;
        } else {
          grid[r][c] = 0; // open pathway
        }
      } else {
        grid[r][c] = 0; // unreachable pocket, leave empty (harmless)
      }
    }
  }

  return { grid, normalBrickCount };
}

function fallbackGrid(rows, cols) {
  const grid = [];
  for (let r = 0; r < rows; r++) {
    const row = [];
    for (let c = 0; c < cols; c++) {
      if (r < 2 || r > rows - 3) row.push(0);
      else if (c === 0 || c === cols - 1) row.push(2);
      else row.push(1);
    }
    grid.push(row);
  }
  return grid;
}

/**
 * Generates a rows x cols brick grid (same 0-4 codes as the game). Retries
 * up to 6 times with a different seed if a roll produces too few
 * destructible bricks (< 18), then falls back to a simple ringed layout -
 * matching LevelGenerator.generate()'s retry/fallback behavior exactly.
 */
export function generateCaveGrid({
  rows,
  cols,
  seed,
  smoothIterations = 4,
  initialSolidChance = 0.45,
  brickFillChance = 0.6,
}) {
  const baseSeed = seed ?? Math.floor(Math.random() * 0xffffffff);
  for (let i = 0; i < 6; i++) {
    const rng = mulberry32((baseSeed + i * 101) >>> 0);
    const { grid, normalBrickCount } = attempt(
      rng,
      rows,
      cols,
      smoothIterations,
      initialSolidChance,
      brickFillChance
    );
    if (normalBrickCount >= 18) return grid;
  }
  return fallbackGrid(rows, cols);
}
